import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// Animated background
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(-45deg, #006699, #764ba2, #f093fb, #f5576c, #4facfe, #00f2fe)',
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 15s ease infinite`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const MaintenanceCard = styled(Card)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.spacing(3),
  backdropFilter: 'blur(20px)',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));

const PulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const AnimatedIcon = styled(Box)({
  animation: `${PulseAnimation} 2s ease-in-out infinite`,
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1rem',
});

const MaintenancePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Simulate progress animation
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => (prevProgress >= 100 ? 0 : prevProgress + 1));
    }, 100);
    return () => clearInterval(timer);
  }, []);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const maintenanceSteps = [
    { icon: <BuildIcon />, text: 'System Updates' },
    { icon: <EngineeringIcon />, text: 'Performance Optimization' },
    { icon: <CheckCircleIcon />, text: 'Quality Assurance' }
  ];

  return (
    <AnimatedBackground>
      <Container maxWidth="md">
        <Fade in timeout={1000}>
          <MaintenanceCard elevation={0}>
            <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
              {/* Maintenance GIF/Animation */}
              <AnimatedIcon>
                <Box
                  component="img"
                  src="https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExc211NXNmcmE5YTVvaHNlZWR2NXp0Z25nZjMyaHlwNDd0ZXBlbGplNCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/rEKVSJUyMaXZlTIaEu/giphy.gif"
                  alt="Maintenance in progress"
                  sx={{
                    width: { xs: 150, sm: 200 },
                    height: { xs: 150, sm: 200 },
                    borderRadius: 2,
                    mb: 2
                  }}
                />
              </AnimatedIcon>

              {/* Status Chip */}
              <Box display="flex" justifyContent="center" mb={2}>
                <Chip
                  icon={<ScheduleIcon />}
                  label="Scheduled Maintenance"
                  color="warning"
                  variant="filled"
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    px: 1
                  }}
                />
              </Box>

              {/* Main Heading */}
              <Zoom in timeout={1500}>
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  component="h1"
                  align="center"
                  gutterBottom
                  sx={{
                    background: 'linear-gradient(45deg, #006699, #764ba2)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  We're Enhancing Your Experience
                </Typography>
              </Zoom>

              {/* Description */}
              <Typography
                variant="h6"
                align="center"
                color="text.secondary"
                paragraph
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                Our platform is currently undergoing scheduled maintenance to bring you
                improved performance and new features.
              </Typography>

              {/* Progress Bar */}
              {/* <Box sx={{ mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Maintenance Progress
                  </Typography>
                  <Typography variant="body2" color="primary.main" fontWeight={600}>
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 5,
                    backgroundColor: 'rgba(103, 126, 234, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: 'linear-gradient(45deg, #006699, #764ba2)'
                    }
                  }}
                />
              </Box> */}

              {/* Maintenance Steps */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" align="center" gutterBottom fontWeight={600}>
                  What we're working on:
                </Typography>
                <Box display="flex" justifyContent="space-around" flexWrap="wrap" gap={2} mt={2}>
                  {maintenanceSteps.map((step, index) => (
                    <Fade in timeout={2000 + index * 500} key={index}>
                      <Box textAlign="center" minWidth={120}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            p: 1.5,
                            borderRadius: '50%',
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            mb: 1
                          }}
                        >
                          {step.icon}
                        </Box>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {step.text}
                        </Typography>
                      </Box>
                    </Fade>
                  ))}
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Time and ETA */}
              {/* <Box textAlign="center" mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current time: {currentTime.toLocaleTimeString()}
                </Typography>
                <Typography variant="body1" color="primary.main" fontWeight={500}>
                  🕐 Estimated completion: 30-60 minutes
                </Typography>
              </Box> */}

              {/* Action Buttons */}
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={2}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(45deg, #006699, #764ba2)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a6fd8, #6a4190)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(103, 126, 234, 0.4)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Check Status
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  href="mailto:help@carepassport.ca"
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: 'primary.main',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: 'primary.dark',
                      backgroundColor: 'rgba(103, 126, 234, 0.04)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Contact Support
                </Button>
              </Box>

              {/* Footer */}
              <Box mt={4} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Thank you for your patience. We'll be back online soon! 🚀
                </Typography>
              </Box>
            </CardContent>
          </MaintenanceCard>
        </Fade>
      </Container>
    </AnimatedBackground>
  );
};

export default MaintenancePage;